import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { ChevronsUpDown, Loader2, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface RemoteOption {
  code: string;
  label: string;
}

interface RemoteSearchSelectProps {
  /** Code sélectionné ('' = aucun). */
  value: string;
  /** Libellé du code sélectionné, pour l'affichage (la sélection vient d'une
   *  recherche distante, donc absente d'une liste pré-chargée). */
  selectedLabel?: string | null;
  onSelect: (code: string, label: string | null) => void;
  /** Recherche distante débouncée. */
  search: (query: string) => Promise<RemoteOption[]>;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Nombre de caractères avant déclenchement (défaut 2). */
  minChars?: number;
  className?: string;
  contentClassName?: string;
}

/**
 * Select à recherche distante : interroge une source au fil de la saisie
 * (débouncée) plutôt que de pré-charger toutes les options. Adapté aux
 * nomenclatures volumineuses (MEF, matières Éducation nationale).
 */
export function RemoteSearchSelect({
  value,
  selectedLabel,
  onSelect,
  search,
  placeholder = 'Sélectionner...',
  searchPlaceholder = 'Rechercher...',
  minChars = 2,
  className,
  contentClassName,
}: RemoteSearchSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [options, setOptions] = React.useState<RemoteOption[]>([]);
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const reqIdRef = React.useRef(0);

  React.useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < minChars) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const reqId = ++reqIdRef.current;
    const handle = setTimeout(() => {
      search(q)
        .then((res) => {
          // Ignore les réponses obsolètes (recherche concurrente).
          if (reqId === reqIdRef.current) setOptions(res);
        })
        .catch(() => {
          if (reqId === reqIdRef.current) setOptions([]);
        })
        .finally(() => {
          if (reqId === reqIdRef.current) setLoading(false);
        });
    }, 300);
    return () => clearTimeout(handle);
  }, [query, open, minChars, search]);

  const display = value ? selectedLabel ?? value : '';

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setQuery('');
          setOptions([]);
        }
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'flex h-9 w-full items-center justify-between',
            'rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm',
            'ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring',
            !display && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">{display || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-50 w-[var(--radix-popover-trigger-width)] rounded-md border bg-popover shadow-md',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            contentClassName,
          )}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <div className="flex items-center border-b px-3">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex h-9 w-full bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground/60"
            />
            {query && (
              <X
                className="h-4 w-4 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
                onClick={() => setQuery('')}
              />
            )}
          </div>

          <div className="max-h-[300px] overflow-y-auto p-1">
            {value && (
              <button
                type="button"
                onClick={() => {
                  onSelect('', null);
                  setOpen(false);
                }}
                className="flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm text-muted-foreground outline-none hover:bg-accent hover:text-accent-foreground"
              >
                (effacer la sélection)
              </button>
            )}
            {loading ? (
              <p className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Recherche…
              </p>
            ) : query.trim().length < minChars ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Saisissez au moins {minChars} caractères
              </p>
            ) : options.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Aucun résultat</p>
            ) : (
              options.map((o) => (
                <button
                  key={o.code}
                  type="button"
                  onClick={() => {
                    onSelect(o.code, o.label);
                    setOpen(false);
                  }}
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                    'hover:bg-accent hover:text-accent-foreground',
                    o.code === value && 'bg-accent',
                  )}
                >
                  <span className="truncate">
                    {o.label}{' '}
                    <span className="font-mono text-xs text-muted-foreground">{o.code}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
